import { createApiClient, createAuthStore, createSessionRedirect, locale } from "@gaming/shared";
import router from "./router";

/**
 * The player site's API client, bound to the member plane.
 *
 * Session is the httpOnly `gp_member` cookie. The player site is the app where
 * XSS risk is real — usernames, chat and promotions are all user-controlled
 * content — which is precisely why the credential is not reachable from
 * JavaScript here.
 */
export const api = createApiClient({
  plane: "player",
  baseURL: "/api",
  // Only a route that actually needs a session may be interrupted by a 401.
  // The lobby and promotions are public, so a member endpoint answering "not
  // signed in" there is expected and must not throw the visitor out.
  // hasSession is read lazily, at 401 time — the store does not exist yet
  // when this runs. Without it an anonymous visitor's ordinary 401 would be
  // announced as an expired session they never had.
  onUnauthorized: createSessionRedirect(router, {
    hasSession: () => !!useAuthStore().user,
    onExpired: () => { useAuthStore().user = null; },
  }),
});

/**
 * The player's language, on every request.
 *
 * Game providers mint their launch URL in a language chosen at call time, so the
 * server has to know which one — and the site's locale lives in the browser, not
 * on the session. Without this a Vietnamese player reading a fully translated
 * site would still have the game itself open in English.
 *
 * A header rather than a query parameter: it applies to every call rather than
 * to one, and it does not end up in access logs attached to a member id.
 */
api.client.interceptors.request.use((config) => {
  config.headers["X-Locale"] = locale.value;
  return config;
});

export const useAuthStore = createAuthStore({
  id: "player-auth",
  api,
  loginPath: "/member/auth/login",
  mfaPath: "/member/auth/mfa",     // members have no MFA yet
  mePath: "/member/auth/me",
  logoutPath: "/member/auth/logout",
});

export const accountApi = {
  // Two-step signup. Nothing is written until completeRegistration, so an
  // abandoned form leaves no half-account behind.
  requestRegistrationOtp: (email) =>
    api.client.post("/member/auth/register/request-otp", { email }).then((r) => r.data),

  verifyRegistrationOtp: (email, code) =>
    api.client.post("/member/auth/register/verify-otp", { email, code }).then((r) => r.data),

  completeRegistration: (payload) =>
    api.client.post("/member/auth/register/complete", payload).then((r) => r.data),
  forgotPassword: (email) =>
    api.client.post("/auth/password/member/forgot", { email }).then((r) => r.data),
  // Recovery is per-plane and keyed by address plus emailed code — there is no
  // opaque token anywhere in the flow, so the address has to be carried from the
  // forgot screen or asked for again.
  resetPassword: (email, code, newPassword) =>
    api.client.post("/auth/password/member/reset", { email, code, newPassword }).then((r) => r.data),
};

export const walletApi = {
  balances: () => api.client.get("/wallet").then((r) => r.data),
  ledger: (limit = 50) => api.client.get("/wallet/ledger", { params: { limit } }).then((r) => r.data),
};

export const cashierApi = {
  methods: () => api.client.get("/cashier/methods").then((r) => r.data),
  createDeposit: (method, amount, network) =>
    api.client.post("/cashier/deposit", { method, amount, network }).then((r) => r.data),
  claimPaid: (id, reference) =>
    api.client.post(`/cashier/deposit/${id}/paid`, { reference }).then((r) => r.data),
  // bankAccountId picks among the member's approved accounts. The server still
  // ignores `destination` for bank payouts — it is only carried for crypto.
  withdraw: (method, amount, destination, network, bankAccountId = null) =>
    api.client
      .post("/cashier/withdraw", { method, amount, destination, network, bankAccountId })
      .then((r) => r.data),
  cancelWithdrawal: (id) =>
    api.client.post(`/cashier/withdraw/${id}/cancel`).then((r) => r.data),
  // Filters go on the query string so a filtered view is a shareable URL and a
  // back gesture returns to what the player was looking at.
  history: (params = {}) =>
    api.client.get("/cashier/history", { params }).then((r) => r.data),

  // Everything standing between this player and a payout, answered before they
  // try. The server re-checks all of it on the request itself — this is for
  // telling them where they stand, never for deciding.
  eligibility: () => api.client.get("/cashier/withdraw/eligibility").then((r) => r.data),
};

export const profileApi = {
  changePassword: (currentPassword, newPassword) =>
    api.client.post("/member/profile/password", { currentPassword, newPassword })
      .then((r) => r.data),

  changeCurrency: (currency) =>
    api.client.post("/member/profile/currency", { currency }).then((r) => r.data),

  // Invite code, the link that carries it, and how many have joined. The link is
  // built server-side from the request host, so each whitelabel hands out its
  // own address rather than a configured one belonging to another brand.
  referral: () => api.client.get("/member/profile/referral").then((r) => r.data),

  // Tier, cumulative deposits, and what the next one needs. Derived server-side
  // from deposits — nothing stores a member's tier, so a threshold change
  // re-tiers everybody on the next load.
  vip: () => api.client.get("/member/profile/vip").then((r) => r.data),
};

export const statementApi = {
  // Computed from settled rounds each time, never a stored counter — a total
  // that disagrees with the ledger is the number a player screenshots.
  get: (params = {}) =>
    api.client.get("/member/statement", { params }).then((r) => r.data),
};

export const rebateApi = {
  // A rate card, not an accrual figure. What you are owed comes from the ledger.
  rates: () => api.client.get("/member/rebate").then((r) => r.data),
};

export const transferApi = {
  summary: () => api.client.get("/cashier/transfer").then((r) => r.data),

  // Releasing bonus into cash is where the play-through requirement is enforced,
  // not at cash-out — see the note on the endpoint.
  bonusToCash: (amount) =>
    api.client.post("/cashier/transfer/bonus-to-cash", { amount }).then((r) => r.data),

  toMember: (username, amount, remark) =>
    api.client.post("/cashier/transfer/member", { username, amount, remark }).then((r) => r.data),
};

export const kycApi = {
  status: () => api.client.get("/member/kyc").then((r) => r.data),
  start: (tier) => api.client.post("/member/kyc/start", { tier }).then((r) => r.data),

  // Bank details replaced document upload. The holder name is matched against
  // the name the player registered under; the response says whether it does, so
  // a mismatch is fixed in the form rather than becoming a rejected case.
  saveBank: (payload) => api.client.post("/member/kyc/bank", payload).then((r) => r.data),

  submit: () => api.client.post("/member/kyc/submit").then((r) => r.data),

  // Never applied directly — an operator reviews it, and the existing account
  // stays in force until they approve.
  requestBankChange: (payload) =>
    api.client.post("/member/kyc/bank-change", payload).then((r) => r.data),
};

export const gamesApi = {
  // The whitelabel is resolved from the hostname server-side, so the catalogue
  // needs no site parameter and cannot be asked for someone else's.
  catalogue: () => api.client.get("/games").then((r) => r.data),

  // Real winning rounds from the last 24 hours, usernames masked. Empty until
  // there is play — the lobby hides the ticker rather than inventing entries.
  // expectUnauthorized: the ticker is decoration. If this endpoint is missing —
  // a UI deployed ahead of the API returns 401 here, not 404, under the
  // deny-by-default policy — the strip should stay hidden, not sign the player
  // out. A cosmetic call must never be able to take the page down.
  // The ticker: every settled round, win or lose. Separate from recentWins,

  // which is a leaderboard — a feed of only winners reads as advertising.

  liveFeed: () => api.client.get("/games/live-feed").then((r) => r.data),

  recentWins: () =>
    api.client.get("/games/recent-wins", { expectUnauthorized: true }).then((r) => r.data),
  launch: (id) => api.client.post(`/games/${id}/launch`).then((r) => r.data),
};

export const notificationsApi = {
  list: () => api.client.get("/member/notifications").then((r) => r.data),
};
